import { defineAsyncComponent } from 'vue';
import { GlButton, GlButtonGroup, GlDisclosureDropdown, GlDisclosureDropdownItem, GlFormCheckbox, GlSprintf, GlLink, GlTooltipDirective, GlSkeletonLoader } from '@gitlab/ui';
import { isEmpty, isNil } from 'lodash-es';
import axios from '~/lib/utils/axios_utils';
import readyToMergeMixin from 'ee_else_ce/vue_merge_request_widget/mixins/ready_to_merge';
import readyToMergeQuery from '~/vue_merge_request_widget/queries/states/merge_widget.query.graphql';
import { createAlert } from '~/alert';
import { fetchPolicies } from '~/lib/graphql';
import { TYPENAME_MERGE_REQUEST } from '~/graphql_shared/constants';
import { STATUS_CLOSED, STATUS_MERGED } from '~/issues/constants';
import { secondsToMilliseconds } from '~/lib/utils/datetime_utility';
import { __, s__, n__, sprintf } from '~/locale';
import SmartInterval from '~/smart_interval';
import { helpPagePath } from '~/helpers/help_page_helper';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import readyToMergeSubscription from '~/vue_merge_request_widget/queries/states/merge_widget.subscription.graphql';
import HelpPopover from '~/vue_shared/components/help_popover.vue';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { rebaseProjectMergeRequestPath } from '~/lib/utils/path_helpers/merge_requests';
import { rebaseFailureMessage } from '~/vue_merge_request_widget/utils';
import { AUTO_MERGE_STRATEGIES, MT_MERGE_STRATEGY, PIPELINE_FAILED_STATE, STATE_MACHINE, MT_SKIP_TRAIN, MT_RESTART_TRAIN, MWCP_MERGE_STRATEGY } from '../constants';
import eventHub from '../event_hub';
import mergeRequestQueryVariablesMixin from '../mixins/merge_request_query_variables';
import MergeRequestStore from '../stores/mr_widget_store';
import AddedCommitMessage from './added_commit_message.vue';
import RelatedLinks from './mr_widget_related_links.vue';
import CommitEdit from './states/commit_edit.vue';
import CommitMessageDropdown from './states/commit_message_dropdown.vue';
import SquashBeforeMerge from './states/squash_before_merge.vue';
import MergeFailedPipelineConfirmationDialog from './states/merge_failed_pipeline_confirmation_dialog.vue';
import RebaseConfirmationDialog from './states/rebase_confirmation_dialog.vue';
const PIPELINE_PENDING_STATE = 'pending';
const PIPELINE_SUCCESS_STATE = 'success';
const MERGE_FAILED_STATUS = 'failed';
const MERGE_SUCCESS_STATUS = 'success';
const MERGE_HOOK_VALIDATION_ERROR_STATUS = 'hook_validation_error';
const { transitions } = STATE_MACHINE;
const { MERGE, MERGE_FAILURE, AUTO_MERGE, MERGING } = transitions;
export default {
    name: 'MergeWidget',
    apollo: {
        state: {
            query: readyToMergeQuery,
            fetchPolicy: fetchPolicies.NO_CACHE,
            variables () {
                return this.mergeRequestQueryVariables;
            },
            manual: true,
            result ({ data }) {
                if (!data?.project?.mergeRequest) {
                    return;
                }
                if (Object.keys(this.state).length === 0) {
                    this.removeSourceBranch = data.project.mergeRequest.shouldRemoveSourceBranch || data.project.mergeRequest.forceRemoveSourceBranch || false;
                    this.commitMessage = data.project.mergeRequest.defaultMergeCommitMessage;
                    this.squashBeforeMerge = data.project.mergeRequest.squashOnMerge;
                    this.isSquashReadOnly = data.project.mergeRequest.squashReadOnly;
                    this.squashCommitMessage = data.project.mergeRequest.defaultSquashCommitMessage;
                }
                this.state = {
                    ...data.project.mergeRequest,
                    mergeRequestsFfOnlyEnabled: data.project.mergeRequestsFfOnlyEnabled,
                    onlyAllowMergeIfPipelineSucceeds: data.project.onlyAllowMergeIfPipelineSucceeds
                };
                this.loading = false;
                if (!this.commitMessageIsTouched) {
                    this.commitMessage = this.state.defaultMergeCommitMessage;
                }
                if (!this.squashCommitMessageIsTouched) {
                    this.squashCommitMessage = this.state.defaultSquashCommitMessage;
                }
                if (!isNil(this.mr.mergeTrainsCount) && !this.pollingInterval) {
                    this.initPolling();
                }
            },
            subscribeToMore: {
                document () {
                    return readyToMergeSubscription;
                },
                skip () {
                    return !this.mr?.id || this.loading;
                },
                variables () {
                    return {
                        issuableId: convertToGraphQLId(TYPENAME_MERGE_REQUEST, this.mr?.id)
                    };
                },
                updateQuery (_, { subscriptionData: { data: { mergeRequestMergeStatusUpdated } } }) {
                    if (mergeRequestMergeStatusUpdated) {
                        this.state = {
                            ...mergeRequestMergeStatusUpdated,
                            mergeRequestsFfOnlyEnabled: this.state.mergeRequestsFfOnlyEnabled,
                            onlyAllowMergeIfPipelineSucceeds: this.state.onlyAllowMergeIfPipelineSucceeds
                        };
                        if (!this.commitMessageIsTouched) {
                            this.commitMessage = mergeRequestMergeStatusUpdated.defaultMergeCommitMessage;
                        }
                        if (!this.squashCommitMessageIsTouched) {
                            this.squashCommitMessage = mergeRequestMergeStatusUpdated.defaultSquashCommitMessage;
                        }
                    }
                }
            }
        }
    },
    components: {
        SquashBeforeMerge,
        CommitEdit,
        CommitMessageDropdown,
        GlSprintf,
        GlLink,
        GlButton,
        GlButtonGroup,
        GlDisclosureDropdown,
        GlDisclosureDropdownItem,
        GlFormCheckbox,
        GlSkeletonLoader,
        MergeFailedPipelineConfirmationDialog,
        RebaseConfirmationDialog,
        MergeImmediatelyConfirmationDialog: defineAsyncComponent(()=>import('ee_component/vue_merge_request_widget/components/merge_immediately_confirmation_dialog.vue')),
        MergeTrainFailedPipelineConfirmationDialog: defineAsyncComponent(()=>import('ee_component/vue_merge_request_widget/components/merge_train_failed_pipeline_confirmation_dialog.vue')),
        MergeTrainRestartTrainConfirmationDialog: defineAsyncComponent(()=>import('ee_component/vue_merge_request_widget/components/merge_train_restart_train_confirmation_dialog.vue')),
        AddedCommitMessage,
        RelatedLinks,
        HelpPopover,
        AiCommitMessage: defineAsyncComponent(()=>import('ee_component/vue_merge_request_widget/components/ai_commit_message.vue'))
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        readyToMergeMixin,
        mergeRequestQueryVariablesMixin,
        glFeatureFlagsMixin()
    ],
    props: {
        mr: {
            type: Object,
            required: true
        },
        service: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            loading: true,
            state: {},
            removeSourceBranch: this.mr.shouldRemoveSourceBranch,
            isMakingRequest: false,
            isMergingImmediately: false,
            commitMessage: this.mr.commitMessage,
            commitMessageIsTouched: false,
            squashBeforeMerge: this.mr.squashIsSelected,
            isSquashReadOnly: this.mr.squashIsReadonly,
            squashCommitMessage: this.mr.squashCommitMessage,
            squashCommitMessageIsTouched: false,
            isPipelineFailedModalVisibleMergeTrain: false,
            isPipelineFailedModalVisibleNormalMerge: false,
            isMergeTrainBeingForceMerged: false,
            mergeTrainMergeType: MT_RESTART_TRAIN,
            skipMergeTrain: false,
            mergeTrainsSkipAllowed: this.mr.mergeTrainsSkipAllowed,
            editCommitMessage: false,
            isRebaseInProgress: false,
            isRebaseModalVisible: false
        };
    },
    computed: {
        hasCI () {
            return this.mr.hasCI;
        },
        isAutoMergeAvailable () {
            return !isEmpty(this.mr.availableAutoMergeStrategies);
        },
        autoMergeStrategiesPending () {
            // Strategies load from a separate poll than this component's own query, so
            // until that poll resolves they are not yet an array (the field is [String]).
            // Treat any non-array value as pending so the button can't trigger an
            // immediate merge (bypassing a merge train) before auto-merge availability
            // is known (#593704).
            return !Array.isArray(this.mr.availableAutoMergeStrategies);
        },
        pipeline () {
            return this.state.headPipeline;
        },
        isPipelineFailed () {
            return [
                'FAILED',
                'CANCELED'
            ].indexOf(this.pipeline?.status) !== -1;
        },
        showMergeFailedPipelineConfirmationDialog () {
            return this.status === PIPELINE_FAILED_STATE && this.isPipelineFailed || this.mr.retargeted;
        },
        canRemoveSourceBranch () {
            return this.state.userPermissions.removeSourceBranch;
        },
        commitTemplateHelpPage () {
            return helpPagePath('user/project/merge_requests/commit_templates.md');
        },
        commitTemplateHintText () {
            if (this.shouldShowSquashEdit && this.shouldShowMergeEdit) {
                return this.$options.i18n.mergeAndSquashCommitTemplatesHintText;
            }
            if (this.shouldShowSquashEdit) {
                return this.$options.i18n.squashCommitTemplateHintText;
            }
            return this.$options.i18n.mergeCommitTemplateHintText;
        },
        commits () {
            return this.state.commitsWithoutMergeCommits?.nodes;
        },
        commitsCount () {
            return this.mr.commitsCount || 0;
        },
        preferredAutoMergeStrategy () {
            return MergeRequestStore.getPreferredAutoMergeStrategy(this.mr.availableAutoMergeStrategies);
        },
        isPreferredAutoMergeStrategyMWPC () {
            return this.preferredAutoMergeStrategy === MWCP_MERGE_STRATEGY;
        },
        squashIsSelected () {
            return this.isSquashReadOnly ? this.state.squashOnMerge : this.state.squash;
        },
        status () {
            const ciStatus = this.pipeline?.status?.toLowerCase();
            if (this.hasCI && !ciStatus || this.hasPipelineMustSucceedConflict) {
                return PIPELINE_FAILED_STATE;
            }
            if (this.isAutoMergeAvailable) {
                return PIPELINE_PENDING_STATE;
            }
            if (this.pipeline && this.isPipelineFailed) {
                return PIPELINE_FAILED_STATE;
            }
            return PIPELINE_SUCCESS_STATE;
        },
        showMergeButtonShimmer () {
            return this.glFeatures.mergeButtonShimmer && !this.isMergeButtonDisabled;
        },
        mergeButtonText () {
            if (this.autoMergeStrategiesPending) {
                return s__('mrWidget|Checking if auto-merge is available…');
            }
            if (this.isMergingImmediately) {
                return __('Merge in progress');
            }
            if (this.isAutoMergeAvailable) {
                return this.autoMergeText;
            }
            if (this.status === PIPELINE_FAILED_STATE || this.isPipelineFailed) {
                return __('Merge…');
            }
            return __('Merge');
        },
        showAutoMergeHelperText () {
            return this.isAutoMergeAvailable;
        },
        hasPipelineMustSucceedConflict () {
            return !this.hasCI && this.state.onlyAllowMergeIfPipelineSucceeds;
        },
        isNotClosed () {
            return this.mr.state !== STATUS_CLOSED;
        },
        isMerged () {
            return this.mr.state === STATUS_MERGED;
        },
        isNeitherClosedNorMerged () {
            return this.mr.state !== STATUS_CLOSED && this.mr.state !== STATUS_MERGED;
        },
        isRemoveSourceBranchButtonDisabled () {
            return this.isMergeButtonDisabled;
        },
        shouldShowSquashBeforeMerge () {
            const { enableSquashBeforeMerge } = this.mr;
            if (this.isSquashReadOnly && !this.squashIsSelected) {
                return false;
            }
            return enableSquashBeforeMerge;
        },
        shouldShowSquashEdit () {
            return this.squashBeforeMerge && this.shouldShowSquashBeforeMerge;
        },
        shouldShowMergeEdit () {
            return !this.state.mergeRequestsFfOnlyEnabled;
        },
        shaMismatchLink () {
            return this.mr.mergeRequestDiffsPath;
        },
        showDangerMessageForMergeTrain () {
            return this.preferredAutoMergeStrategy === MT_MERGE_STRATEGY && this.isPipelineFailed;
        },
        shouldShowMergeControls () {
            return this.mr.canMerge && !this.mr.autoMergeEnabled && this.mr.state === 'readyToMerge';
        },
        sourceBranchPreMergeText () {
            return this.removeSourceBranch && !this.mr.sourceBranchProtected ? __('Source branch will be deleted.') : __('Source branch will not be deleted.');
        },
        sourceBranchMergedText () {
            return this.mr.sourceBranchRemoved ? __('Deleted the source branch.') : __('Did not delete the source branch.');
        },
        sourceHasDivergedFromTarget () {
            return this.mr.divergedCommitsCount > 0;
        },
        showMergeDetailsHeader () {
            return ![
                'readyToMerge'
            ].includes(this.mr.state);
        },
        autoMergeHelpPopoverOptions () {
            return {
                title: this.autoMergePopoverSettings.title
            };
        },
        isSkipMergeTrainAvailable () {
            return this.mergeTrainsSkipAllowed && this.glFeatures.mergeTrainsSkipTrain;
        },
        displaySkipMergeTrainOptions () {
            return this.shouldDisplayMergeImmediatelyDropdownOptions && this.isSkipMergeTrainAvailable;
        },
        canRebase () {
            return this.sourceHasDivergedFromTarget && this.mr.canPushToSourceBranch;
        },
        showAutomaticRebaseInfo () {
            return this.mr.showAutomaticRebaseInfo;
        },
        rebaseButtonLabel () {
            return this.showAutomaticRebaseInfo ? __('Rebase with pipeline') : __('Rebase source branch');
        }
    },
    watch: {
        'mr.state': function mrStateWatcher() {
            this.isMakingRequest = false;
        },
        'state.autoMergeEnabled': function mrAutoMergeEnabledWatcher() {
            this.isMakingRequest = false;
        }
    },
    mounted () {
        eventHub.$on('ApprovalUpdated', this.updateGraphqlState);
        eventHub.$on('mr-widget-update-requested', this.updateGraphqlState);
    },
    beforeDestroy () {
        eventHub.$off('ApprovalUpdated', this.updateGraphqlState);
        eventHub.$off('mr-widget-update-requested', this.updateGraphqlState);
        if (this.pollingInterval) {
            this.pollingInterval.destroy();
        }
    },
    methods: {
        initPolling () {
            const startingPollInterval = secondsToMilliseconds(5);
            this.pollingInterval = new SmartInterval({
                callback: ()=>this.$apollo.queries.state.refetch(),
                startingInterval: startingPollInterval,
                maxInterval: startingPollInterval + secondsToMilliseconds(4 * 60),
                hiddenInterval: secondsToMilliseconds(6 * 60),
                incrementByFactorOf: 2
            });
        },
        updateGraphqlState () {
            return this.$apollo.queries.state.refetch();
        },
        handleMergeButtonClick (useAutoMerge, mergeImmediately = false, confirmationClicked = false) {
            if (this.preferredAutoMergeStrategy !== MT_MERGE_STRATEGY && this.showMergeFailedPipelineConfirmationDialog && !confirmationClicked) {
                this.isPipelineFailedModalVisibleNormalMerge = true;
                return;
            }
            if (this.showFailedPipelineModalMergeTrain && !confirmationClicked) {
                this.isPipelineFailedModalVisibleMergeTrain = true;
                return;
            }
            if (mergeImmediately) {
                this.isMergingImmediately = true;
            }
            const latestSha = this.state.diffHeadSha;
            const options = {
                sha: latestSha || this.mr.sha,
                auto_merge_strategy: useAutoMerge ? this.preferredAutoMergeStrategy : undefined,
                should_remove_source_branch: this.removeSourceBranch === true,
                squash: this.squashBeforeMerge,
                skip_merge_train: this.skipMergeTrain
            };
            // If users can't alter the squash message (e.g. for 1-commit merge requests),
            // we shouldn't send the commit message because that would make the backend
            // do unnecessary work.
            if (this.shouldShowSquashBeforeMerge && this.squashCommitMessageIsTouched) {
                options.squash_commit_message = this.squashCommitMessage;
            }
            if (this.commitMessageIsTouched) {
                options.commit_message = this.commitMessage;
            }
            this.isMakingRequest = true;
            this.editCommitMessage = false;
            if (!useAutoMerge) {
                this.mr.transitionStateMachine({
                    transition: MERGE
                });
            }
            this.service.merge(options).then((res)=>res.data).then((data)=>{
                const hasError = data.status === MERGE_FAILED_STATUS || data.status === MERGE_HOOK_VALIDATION_ERROR_STATUS;
                if (AUTO_MERGE_STRATEGIES.includes(data.status)) {
                    eventHub.$emit('mr-widget-update-requested');
                    this.mr.transitionStateMachine({
                        transition: AUTO_MERGE
                    });
                } else if (data.status === MERGE_SUCCESS_STATUS) {
                    this.mr.transitionStateMachine({
                        transition: MERGING
                    });
                } else {
                    this.isMakingRequest = false;
                    this.isMergingImmediately = false;
                    if (hasError) {
                        eventHub.$emit('failed-to-merge', data.merge_error);
                    }
                    eventHub.$emit('mr-widget-check-status');
                    this.mr.transitionStateMachine({
                        transition: MERGE_FAILURE
                    });
                }
                this.updateGraphqlState();
            }).catch(()=>{
                this.isMakingRequest = false;
                this.mr.transitionStateMachine({
                    transition: MERGE_FAILURE
                });
                createAlert({
                    message: __('Something went wrong. Please try again.')
                });
            });
        },
        handleMergeImmediatelyButtonClick () {
            if (this.isMergeImmediatelyDangerous) {
                this.$refs.confirmationDialog.show();
            } else {
                this.handleMergeButtonClick(false, true);
            }
        },
        handleMergeTrainMergeImmediatelyButtonClick (type) {
            this.mergeTrainMergeType = type;
            this.isMergeTrainBeingForceMerged = true;
        },
        processMergeTrain () {
            if (this.mergeTrainMergeType === MT_SKIP_TRAIN) {
                this.skipMergeTrain = true;
            }
            this.handleMergeButtonClick(false, true, true);
        },
        onMergeImmediatelyConfirmation () {
            this.handleMergeButtonClick(false, true, true);
        },
        onMergeWithFailedPipelineConfirmation () {
            this.handleMergeButtonClick(false, true, true);
        },
        setCommitMessage (val) {
            this.commitMessage = val;
            this.commitMessageIsTouched = true;
        },
        setSquashCommitMessage (val) {
            this.squashCommitMessage = val;
            this.squashCommitMessageIsTouched = true;
        },
        appendCommitMessage (messageType, val) {
            this[`${messageType}Message`] = `${this[`${messageType}Message`]}\n\n${val}`;
            this[`${messageType}MessageIsTouched`] = true;
        },
        updateRemoveSourceBranchSettings (checked) {
            this.removeSourceBranch = checked;
            this.mr.setRemoveSourceBranch(checked);
        },
        handleRebaseClick () {
            this.isRebaseModalVisible = true;
        },
        async rebaseConfirmed () {
            if (this.isRebaseDisabled) return;
            try {
                this.isRebaseInProgress = true;
                const rebasePath = rebaseProjectMergeRequestPath(this.mr.targetProjectFullPath, this.mr.iid);
                await axios.post(rebasePath);
                createAlert({
                    message: sprintf(this.$options.i18n.scheduledRebase, {
                        branch: this.mr.sourceBranch
                    }),
                    variant: 'success'
                });
                this.updateGraphqlState();
            } catch (error) {
                createAlert({
                    message: rebaseFailureMessage(error),
                    variant: 'danger'
                });
            } finally{
                this.isRebaseInProgress = false;
            }
        }
    },
    i18n: {
        mergeCommitTemplateHintText: s__('mrWidget|To change this default message, edit the template for merge commit messages. %{linkStart}Learn more%{linkEnd}.'),
        squashCommitTemplateHintText: s__('mrWidget|To change this default message, edit the template for squash commit messages. %{linkStart}Learn more%{linkEnd}.'),
        mergeAndSquashCommitTemplatesHintText: s__('mrWidget|To change these default messages, edit the templates for both the merge and squash commit messages. %{linkStart}Learn more%{linkEnd}.'),
        sourceDivergedFromTargetText: s__('mrWidget|The source branch is %{link} the target branch.'),
        divergedCommits: (count)=>n__('%d commit behind', '%d commits behind', count),
        scheduledRebase: s__('mrWidget|Scheduled a rebase of branch %{branch}.')
    },
    MT_SKIP_TRAIN,
    MT_RESTART_TRAIN
};
